import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { toPageError } from '../partners/PartnerListPage';
import { defaultWalletListQuery, walletsRepositoryToken } from './repository';
import type { WalletListFilters, WalletListQuery, WalletSummary } from './types';
import { formatWalletMoney } from './types';

export function WalletListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(walletsRepositoryToken);
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [query, setQuery] = useState(() => initialQuery(searchParams));
  const [pageIndex, setPageIndex] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<readonly string[]>(['']);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0 && query.cursor === '',
  );
  const columns = useMemo(() => walletColumns(language, t), [language, t]);

  const apply = (filters: WalletListFilters, sort: WalletListQuery['sort']) => {
    setPageIndex(0);
    setCursorHistory(['']);
    setQuery({ cursor: '', pageSize: query.pageSize, sort, filters });
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('wallets.subtitle')}
        title={t('wallets.title')}
      />
      <WalletListFiltersForm onApply={apply} value={query} />
      <PageState
        emptyMessage={t('wallets.empty')}
        error={toPageError(resource.error, t('wallets.errors.list'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            {mobile ? (
              <CompactWalletResults language={language} wallets={resource.data.items} />
            ) : (
              <Box
                aria-label={t('wallets.results')}
                role="region"
                sx={{ maxWidth: '100%', overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 520, minWidth: 2500 }}>
                  <DataGrid
                    aria-label={t('wallets.results')}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.items}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button
                disabled={pageIndex === 0}
                onClick={() => {
                  const previous = pageIndex - 1;
                  setPageIndex(previous);
                  setQuery((current) => ({ ...current, cursor: cursorHistory[previous] ?? '' }));
                }}
              >
                {t('wallets.pagination.previous')}
              </Button>
              <Button
                disabled={resource.data.nextCursor === null}
                onClick={() => {
                  const cursor = resource.data?.nextCursor;
                  if (cursor === null || cursor === undefined) return;
                  const next = pageIndex + 1;
                  setCursorHistory((current) => [...current.slice(0, next), cursor]);
                  setPageIndex(next);
                  setQuery((current) => ({ ...current, cursor }));
                }}
              >
                {t('wallets.pagination.next')}
              </Button>
            </Stack>
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function WalletListFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: WalletListFilters, sort: WalletListQuery['sort']) => void;
  readonly value: WalletListQuery;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<
    WalletListFilters & { readonly sort: WalletListQuery['sort'] }
  >({ defaultValues: { ...value.filters, sort: value.sort } });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      partnerId: value.filters.partnerId,
      agentId: value.filters.agentId,
      partnerUserId: value.filters.partnerUserId,
      reconciliationStatus: value.filters.reconciliationStatus,
      minimumAvailableBalance: value.filters.minimumAvailableBalance,
      maximumAvailableBalance: value.filters.maximumAvailableBalance,
      minimumReservedBalance: value.filters.minimumReservedBalance,
      maximumReservedBalance: value.filters.maximumReservedBalance,
      sort: value.sort,
    },
    {
      partnerId: defaultWalletListQuery.filters.partnerId,
      agentId: defaultWalletListQuery.filters.agentId,
      partnerUserId: defaultWalletListQuery.filters.partnerUserId,
      reconciliationStatus: defaultWalletListQuery.filters.reconciliationStatus,
      minimumAvailableBalance: defaultWalletListQuery.filters.minimumAvailableBalance,
      maximumAvailableBalance: defaultWalletListQuery.filters.maximumAvailableBalance,
      minimumReservedBalance: defaultWalletListQuery.filters.minimumReservedBalance,
      maximumReservedBalance: defaultWalletListQuery.filters.maximumReservedBalance,
      sort: defaultWalletListQuery.sort,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      applyLabel={t('wallets.filters.apply')}
      ariaLabel={t('filterPanel.label', { page: t('wallets.title') })}
      onClear={() => {
        reset({ ...defaultWalletListQuery.filters, sort: defaultWalletListQuery.sort });
        onApply(defaultWalletListQuery.filters, defaultWalletListQuery.sort);
      }}
      onSubmit={handleSubmit(({ sort, ...filters }) => onApply(normalizeFilters(filters), sort))}
      primaryFields={
        <>
          <TextField label={t('wallets.fields.username')} size="small" {...register('username')} />
          <TextField label={t('wallets.fields.memberId')} size="small" {...register('memberId')} />
          <TextField
            defaultValue=""
            label={t('wallets.fields.status')}
            select
            size="small"
            {...register('status')}
          >
            <MenuItem value="">{t('wallets.filters.all')}</MenuItem>
            {['ACTIVE', 'SUSPENDED', 'CLOSED'].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('wallets.fields.currency')} size="small" {...register('currency')} />
        </>
      }
      secondaryFields={
        <>
          <TextField
            label={t('wallets.fields.partnerId')}
            size="small"
            {...register('partnerId')}
          />
          <TextField label={t('wallets.fields.agentId')} size="small" {...register('agentId')} />
          <TextField
            label={t('wallets.fields.partnerUserId')}
            size="small"
            {...register('partnerUserId')}
          />
          <TextField
            defaultValue=""
            label={t('wallets.fields.reconciliation')}
            select
            size="small"
            {...register('reconciliationStatus')}
          >
            <MenuItem value="">{t('wallets.filters.all')}</MenuItem>
            {['MATCHED', 'MISMATCHED'].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('wallets.filters.minimumAvailable')}
            size="small"
            {...register('minimumAvailableBalance')}
          />
          <TextField
            label={t('wallets.filters.maximumAvailable')}
            size="small"
            {...register('maximumAvailableBalance')}
          />
          <TextField
            label={t('wallets.filters.minimumReserved')}
            size="small"
            {...register('minimumReservedBalance')}
          />
          <TextField
            label={t('wallets.filters.maximumReserved')}
            size="small"
            {...register('maximumReservedBalance')}
          />
          <TextField
            defaultValue={defaultWalletListQuery.sort}
            label={t('wallets.filters.sort')}
            select
            size="small"
            {...register('sort')}
          >
            <MenuItem value="updatedAt">{t('wallets.fields.updatedAt')}</MenuItem>
            <MenuItem value="createdAt">{t('wallets.fields.createdAt')}</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

function CompactWalletResults({
  language,
  wallets,
}: {
  readonly language: TimeLanguage;
  readonly wallets: readonly WalletSummary[];
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('wallets.results')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {wallets.map((wallet) => (
        <Paper component="li" key={wallet.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1}>
              <WalletStatusChip status={wallet.status} />
              <Chip label={wallet.reconciliation.status} size="small" variant="outlined" />
            </Stack>
            <MuiLink component={Link} to={`/affiliate-wallets/${wallet.id}`}>
              {wallet.username ?? wallet.id}
            </MuiLink>
            <Typography variant="body2">
              {wallet.partner.code} · {wallet.agent?.code ?? t('common.notAvailable')}
            </Typography>
            <Typography variant="body2">
              {t('wallets.fields.partnerUserId')}:{' '}
              {wallet.partnerUserId ?? t('common.notAvailable')}
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {t('wallets.fields.total')}:{' '}
              {formatWalletMoney(
                wallet.totalBalance,
                wallet.currencyCode,
                wallet.currencyMinorUnit,
              )}
            </Typography>
            <Typography variant="body2">
              {t('wallets.fields.available')}:{' '}
              {formatWalletMoney(
                wallet.availableBalance,
                wallet.currencyCode,
                wallet.currencyMinorUnit,
              )}
            </Typography>
            <Typography variant="body2">
              {t('wallets.fields.reserved')}:{' '}
              {formatWalletMoney(
                wallet.reservedBalance,
                wallet.currencyCode,
                wallet.currencyMinorUnit,
              )}
            </Typography>
            {[
              ['lifetimeEarned', wallet.lifetimeEarned],
              ['lifetimeReversed', wallet.lifetimeReversed],
              ['lifetimeAdjusted', wallet.lifetimeAdjusted],
              ['lifetimeWithdrawn', wallet.lifetimeWithdrawn],
            ].map(([field, amount]) => (
              <Typography key={field} variant="body2">
                {t(`wallets.fields.${field}`)}:{' '}
                {formatWalletMoney(amount ?? '0', wallet.currencyCode, wallet.currencyMinorUnit)}
              </Typography>
            ))}
            <Typography color="text.secondary" variant="caption">
              {t('wallets.fields.lastEntryAt')}:{' '}
              {wallet.lastEntryAt === null
                ? t('common.notAvailable')
                : formatWithOffset(wallet.lastEntryAt, language)}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function walletColumns(
  language: TimeLanguage,
  t: (key: string) => string,
): readonly GridColDef<WalletSummary>[] {
  const money = (
    field: keyof Pick<
      WalletSummary,
      | 'availableBalance'
      | 'reservedBalance'
      | 'totalBalance'
      | 'lifetimeEarned'
      | 'lifetimeReversed'
      | 'lifetimeAdjusted'
      | 'lifetimeWithdrawn'
    >,
    label: string,
  ): GridColDef<WalletSummary> => ({
    field,
    headerName: label,
    minWidth: 155,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => formatWalletMoney(row[field], row.currencyCode, row.currencyMinorUnit),
  });
  return [
    {
      field: 'username',
      headerName: t('wallets.fields.username'),
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <MuiLink component={Link} to={`/affiliate-wallets/${row.id}`}>
          {row.username ?? row.id}
        </MuiLink>
      ),
    },
    {
      field: 'partner',
      headerName: t('wallets.fields.partner'),
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => `${row.partner.code} · ${row.partner.name}`,
      renderCell: ({ row }) => (
        <MuiLink component={Link} to={`/partners/${row.partner.id}`}>
          {row.partner.code} · {row.partner.name}
        </MuiLink>
      ),
    },
    {
      field: 'agent',
      headerName: t('wallets.fields.agent'),
      minWidth: 160,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => row.agent?.code ?? '',
      renderCell: ({ row }) =>
        row.agent === null ? (
          t('common.notAvailable')
        ) : (
          <MuiLink component={Link} to={`/agents/${row.agent.id}`}>
            {row.agent.code}
          </MuiLink>
        ),
    },
    {
      field: 'partnerUserId',
      headerName: t('wallets.fields.partnerUserId'),
      minWidth: 170,
      sortable: false,
    },
    { field: 'currencyCode', headerName: t('wallets.fields.currency'), width: 95, sortable: false },
    {
      field: 'status',
      headerName: t('wallets.fields.status'),
      width: 120,
      sortable: false,
      renderCell: ({ row }) => <WalletStatusChip status={row.status} />,
    },
    money('availableBalance', t('wallets.fields.available')),
    money('reservedBalance', t('wallets.fields.reserved')),
    money('totalBalance', t('wallets.fields.total')),
    money('lifetimeEarned', t('wallets.fields.lifetimeEarned')),
    money('lifetimeReversed', t('wallets.fields.lifetimeReversed')),
    money('lifetimeAdjusted', t('wallets.fields.lifetimeAdjusted')),
    money('lifetimeWithdrawn', t('wallets.fields.lifetimeWithdrawn')),
    {
      field: 'lastEntryAt',
      headerName: t('wallets.fields.lastEntryAt'),
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) =>
        row.lastEntryAt === null
          ? t('common.notAvailable')
          : formatTimestamp(row.lastEntryAt, 'Asia/Bangkok', language).display,
    },
    {
      field: 'reconciliation',
      headerName: t('wallets.fields.reconciliation'),
      width: 140,
      sortable: false,
      valueGetter: (_value, row) => row.reconciliation.status,
      renderCell: ({ row }) => (
        <Chip
          color={row.reconciliation.status === 'MATCHED' ? 'success' : 'warning'}
          label={row.reconciliation.status}
          size="small"
        />
      ),
    },
  ];
}

export function WalletStatusChip({ status }: { readonly status: WalletSummary['status'] }) {
  return (
    <Chip
      color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}
      label={status}
      size="small"
    />
  );
}

function normalizeFilters(filters: WalletListFilters): WalletListFilters {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [key, value.trim()]),
  ) as unknown as WalletListFilters;
}

function initialQuery(searchParams: URLSearchParams): WalletListQuery {
  return {
    ...defaultWalletListQuery,
    filters: {
      ...defaultWalletListQuery.filters,
      partnerId: searchParams.get('partnerId') ?? '',
      agentId: searchParams.get('agentId') ?? '',
      memberId: searchParams.get('memberId') ?? '',
      username: searchParams.get('username') ?? '',
      currency: searchParams.get('currency') ?? '',
      status: (searchParams.get('status') as WalletListFilters['status'] | null) ?? '',
    },
  };
}

function formatWithOffset(timestamp: string, language: TimeLanguage) {
  const formatted = formatTimestamp(timestamp, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}
